/**
 * MBA Lab — the Coaching Question Deck's GROW-anchored question bank. Pure
 * data, no functions, matching readiness-dimensions.js's precedent. Every
 * question here is original, written from the public GROW structure (Goal,
 * Reality, Options, Will - see Content/Leadership/the-grow-coaching-model.md)
 * - never read or adapted from any third-party question list.
 */

export const STEPS = Object.freeze([
  {
    key: 'goal',
    label: 'Goal',
    helpText: 'Get clear on what this specific conversation needs to accomplish.',
  },
  {
    key: 'reality',
    label: 'Reality',
    helpText: "Get an accurate picture of what's actually happening - not a flattering or catastrophizing one.",
  },
  {
    key: 'options',
    label: 'Options',
    helpText: 'Generate real possibilities before evaluating any of them.',
  },
  {
    key: 'will',
    label: 'Will',
    helpText: 'Turn the conversation into a specific, checkable commitment.',
  },
]);

export const QUESTIONS = Object.freeze([
  // Goal
  { id: 'g-1', step: 'goal', text: 'What would make this conversation worth having, for you?' },
  { id: 'g-2', step: 'goal', text: "If we only get through one thing today, what should it be?" },
  { id: 'g-3', step: 'goal', text: "What does a genuinely good outcome look like here - not the easy one, the good one?" },
  { id: 'g-4', step: 'goal', text: 'Whose goal is this, really - yours, mine, or theirs?' },
  { id: 'g-5', step: 'goal', text: "What's the smallest version of success you'd still count as real progress?" },
  { id: 'g-6', step: 'goal', text: 'Is this a decision we need to make today, or a direction we need to agree on?' },
  { id: 'g-7', step: 'goal', text: 'What happens if we leave this conversation without resolving anything?' },
  { id: 'g-8', step: 'goal', text: 'A month from now, how will you know this conversation actually helped?' },
  // Reality
  { id: 'r-1', step: 'reality', text: 'What have you already tried?' },
  { id: 'r-2', step: 'reality', text: "What's actually gotten in the way, specifically?" },
  { id: 'r-3', step: 'reality', text: "How would someone else describe this situation, if they weren't you?" },
  { id: 'r-4', step: 'reality', text: "What part of this is fact, and what part is your read on it?" },
  { id: 'r-5', step: 'reality', text: "What's the piece of this you haven't said out loud yet?" },
  { id: 'r-6', step: 'reality', text: 'On a scale that\'s honest, not diplomatic, how bad is this really?' },
  { id: 'r-7', step: 'reality', text: 'What\'s changed since the last time this came up?' },
  { id: 'r-8', step: 'reality', text: 'What would you need to admit is true for this to make sense?' },
  // Options
  { id: 'o-1', step: 'options', text: 'Setting aside whether it\'s realistic - what would you do if nothing were in your way?' },
  { id: 'o-2', step: 'options', text: "What else could you try, besides the first thing that came to mind?" },
  { id: 'o-3', step: 'options', text: "What's an option you've already ruled out - and why, exactly?" },
  { id: 'o-4', step: 'options', text: 'If you had to pick two options instead of one, what would they be?' },
  { id: 'o-5', step: 'options', text: 'What would the bravest version of this look like?' },
  { id: 'o-6', step: 'options', text: 'What would you tell a friend in your exact position to do?' },
  { id: 'o-7', step: 'options', text: "Which of these options is easiest to reverse if it turns out to be wrong?" },
  { id: 'o-8', step: 'options', text: "What haven't we considered yet because it seemed off the table?" },
  // Will
  { id: 'w-1', step: 'will', text: 'What, specifically, will you do - not "try to do" - by when?' },
  { id: 'w-2', step: 'will', text: "How will you know it actually happened, not just that you meant to?" },
  { id: 'w-3', step: 'will', text: 'What could get in the way of this actually happening?' },
  { id: 'w-4', step: 'will', text: 'What\'s the very first step - today or tomorrow, not eventually?' },
  { id: 'w-5', step: 'will', text: 'Who else needs to know about this commitment for it to stick?' },
  { id: 'w-6', step: 'will', text: 'On a scale of 1-10, how confident are you this will actually happen - and what would move that number up?' },
  { id: 'w-7', step: 'will', text: 'When should we check back in on this?' },
  { id: 'w-8', step: 'will', text: 'What would make you quietly let this slide without telling me?' },
]);
